import { BountyDataState } from 'interfaces/bounty'
import {
  GET_REDEMPTIONS,
  GET_REWARDS,
  SET_REDEMPTION_STEP_NUMBER,
  TOGGLE_DISMISS_REWARD,
  GET_REFERRAL_INFO,
  SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE,
  SetRewardSummaryAction,
  GET_REWARDS_SUMMARY,
} from 'actions/bountyActions'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { Reducer } from 'interfaces'
import { combineReducers } from 'redux'
import { needDiscardData, REDEMPTIONS_DISCARD_TRIGGERS, REWARD_BALANCE_DISCARD_TRIGGERS, REWARDS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_REDEMPTION } from 'constants/forms'
import { extractRedemptionPayload, extractRewardSummaryPayload } from 'helpers/bountyHelpers'
import { mergeChartNewPoints, normalizeRewardItem, normalizeSummary } from 'components/features/rewards/rewardPage/helpers'
import { RewardSummaryDataState } from 'components/features/rewards/rewardPage/store'
import { WsMessageAction } from 'actions/webSocketActions'
import { concatCollections, normalize } from 'helpers/normalizeHelpers'
import { BuyCryptoAction, SellCryptoAction } from 'components/features/rewards/rewardsPane/store/actions/paneState'

const DEFAULT_STATE: BountyDataState = {
  reward: {
    items: null,
    summary: null,
  },
  redemption: {
    items: null,
    account: null,
  },
  meta: null,
  referral: null,
}

const rewardItemsReducer: Reducer<BountyDataState['reward']['items']> = (prevState = DEFAULT_STATE.reward.items, action) => {
  if (needDiscardData(REWARDS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_REWARDS && isSuccessAction(action)) {
    return action.payload.response
  }

  if (action.type === WsMessageAction.type) {
    if (action.payload.event !== 'reward_transaction_changed' || !prevState || !prevState.results) {
      return prevState
    }

    const newCollection = normalize([normalizeRewardItem(action.payload.data.transaction)], 'uniqKey')

    return {
      ...prevState,
      results: concatCollections(prevState.results, newCollection),
    }
  }

  if (action.type === BuyCryptoAction.type || action.type === SellCryptoAction.type) {
    if (!prevState || action.meta !== 'success') {
      return prevState
    }

    const transaction = {
      transaction_type: 'crypto',
      ...action.payload.response.order,
    } as const

    const newCollection = normalize([normalizeRewardItem(transaction)], 'uniqKey')

    return {
      ...prevState,
      results: concatCollections(newCollection, prevState.results),
    }
  }

  return prevState
}

const rewardSummaryReducer: Reducer<BountyDataState['reward']['summary']> = (prevState = DEFAULT_STATE.reward.summary, action) => {
  if (needDiscardData(REWARDS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  switch (action.type) {
    case GET_REWARDS_SUMMARY: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      return normalizeSummary(action.payload.response)
    }

    case SetRewardSummaryAction.type: {

      if (!prevState) {
        return prevState
      }

      const {
        trend: newTrend,
        lifetime_earnings: newLifetimeEarnings,
        ...restNewSummary
      } = action.payload

      let {
        trend,
        lifetime_earnings,
        // tslint:disable-next-line
        ...restSummary // eslint-disable-line prefer-const
      } = prevState

      if (newTrend && trend) {
        trend = mergeChartNewPoints(trend, newTrend.usd, newTrend.crypto)
      }

      if (newLifetimeEarnings && lifetime_earnings) {
        lifetime_earnings = mergeChartNewPoints(lifetime_earnings, newLifetimeEarnings.rewards_total, newLifetimeEarnings.crypto_profit)
      }

      return {
        ...restSummary,
        ...normalizeSummary(restNewSummary),
        trend,
        lifetime_earnings,
      }
    }

    case SellCryptoAction.type:
    case BuyCryptoAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      if (!prevState) {
        return prevState
      }

      const {
        trend: newTrend,
        lifetime_earnings: newLifetimeEarnings,
        ...restNewSummary
      } = action.payload.response.reward_account

      let {
        trend,
        lifetime_earnings,
        // tslint:disable-next-line
        ...restSummary // eslint-disable-line prefer-const
      } = prevState

      if (newTrend && trend) {
        trend = mergeChartNewPoints(trend, newTrend.usd, newTrend.crypto)
      }

      if (newLifetimeEarnings && lifetime_earnings) {
        lifetime_earnings = mergeChartNewPoints(lifetime_earnings, newLifetimeEarnings.rewards_total, newLifetimeEarnings.crypto_profit)
      }

      return {
        ...restSummary,
        ...normalizeSummary(restNewSummary),
        trend,
        lifetime_earnings,
      }
    }

    case WsMessageAction.type: {
      if (action.payload.event !== 'reward_balances_changed' || !prevState) {
        return prevState
      }

      const { reward_account } = action.payload.data
      // TODO: remove condition after release 6.1
      if (!reward_account) {
        return prevState
      }

      const {
        trend: newTrend,
        lifetime_earnings: newLifetimeEarnings,
        ...restNewSummary
      } = reward_account

      let {
        trend,
        lifetime_earnings,
        // tslint:disable-next-line
        ...restSummary // eslint-disable-line prefer-const
      } = prevState

      if (newTrend && trend) {
        trend = mergeChartNewPoints(trend, newTrend.usd, newTrend.crypto)
      }

      if (newLifetimeEarnings && lifetime_earnings) {
        lifetime_earnings = mergeChartNewPoints(lifetime_earnings, newLifetimeEarnings.rewards_total, newLifetimeEarnings.crypto_profit)
      }

      return {
        ...restSummary,
        ...normalizeSummary(restNewSummary),
        trend,
        lifetime_earnings,
      }
    }
  }

  if (action.type === SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE) {
    if (prevState === null) {
      return null
    }

    const { is_autoinvest_on, autoinvest_ticker } = action.payload
    const result: RewardSummaryDataState = {
      ...prevState,
      autoinvest_ticker,
      is_autoinvest_on,
    }

    return result
  }

  if (action.type === TOGGLE_DISMISS_REWARD && prevState) {
    const { params } = action.payload
    const increment = params.is_dismissed ? 1 : -1

    if (params.status !== 'pending' && params.status !== 'confirmed') {
      return prevState
    }

    if (isSuccessAction(action)) {
      return {
        ...prevState,
        rewardsInProgressCount: prevState.rewardsInProgressCount + increment,
      }
    }
  }

  return prevState
}

const redemptionAccountReducer: Reducer<BountyDataState['redemption']['account']> = (state = DEFAULT_STATE.redemption.account, action) => {
  const payload = extractRedemptionPayload(action)
  if (payload) {
    // we need to distinguish the default value (null) and the case when API returns null
    return payload.account || false
  }

  if (action.type === SET_REDEMPTION_STEP_NUMBER && action.payload === null) {
    return null
  }

  return state
}

const metaReducer: Reducer<BountyDataState['meta']> = (state = DEFAULT_STATE.meta, action) => {
  const payload = extractRedemptionPayload(action) || extractRewardSummaryPayload(action)

  if (needDiscardData(REWARD_BALANCE_DISCARD_TRIGGERS)(action)) {
    return DEFAULT_STATE.meta
  }

  if (payload) {
    return {
      minRedemptionAmount: Math.ceil(+payload.min_redemption_amount),
      rewardBalance: +payload.reward_balance,
    }
  }

  if (state !== null
    && action.type === FORM_SUBMITTED_SUCCESSFULLY
    && action.payload.formName === FORM_REDEMPTION) {
    return {
      ...state,
      rewardBalance: state.rewardBalance - Math.ceil(+action.payload.response.amount),
    }
  }

  return state
}

const redemptionItemsReducer: Reducer<BountyDataState['redemption']['items']> = (state = DEFAULT_STATE.redemption.items, action) => {
  if (needDiscardData(REDEMPTIONS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_REDEMPTIONS && isSuccessAction(action)) {
    return action.payload.response
  }

  return state
}

const referralReducer: Reducer<BountyDataState['referral']> = (state = DEFAULT_STATE.referral, action) => {
  if (action.type === GET_REFERRAL_INFO && isSuccessAction(action)) {
    return action.payload.response
  }

  return state
}

export default combineReducers<BountyDataState>({
  reward: combineReducers({
    items: rewardItemsReducer,
    summary: rewardSummaryReducer,
  }),
  redemption: combineReducers({
    items: redemptionItemsReducer,
    account: redemptionAccountReducer,
  }),
  meta: metaReducer,
  referral: referralReducer,
})
